/**
 * Web Vitals instrumentation for frontend observability.
 * Sprint 31: Core Web Vitals (CWV) collection and reporting.
 * 
 * Collects: LCP (Largest Contentful Paint), INP (Interaction to Next Paint),
 *           CLS (Cumulative Layout Shift), FCP (First Contentful Paint),
 *           TTFB (Time to First Byte)
 * 
 * Metrics are sent to the API endpoint /api/v1/telemetry/web-vitals
 * for aggregation into the observability pipeline.
 */
import { onCLS, onINP, onLCP, onFCP, onTTFB } from 'web-vitals';
import type { Metric } from 'web-vitals';

// API endpoint for reporting metrics (configure via environment)
const VITALS_ENDPOINT = '/api/v1/telemetry/web-vitals';

// Whether to log metrics to console in development
const DEBUG_VITALS = import.meta.env.DEV;

/**
 * Web Vitals metric payload structure for API submission.
 */
interface VitalsPayload {
  name: string;
  value: number;
  rating: 'good' | 'needs-improvement' | 'poor';
  delta: number;
  id: string;
  navigationType: string;
  page: string;
  timestamp: number;
  userAgent: string;
}

/**
 * Formats a metric into the API payload structure.
 */
function formatMetric(metric: Metric): VitalsPayload {
  return {
    name: metric.name,
    value: Math.round(metric.value * 100) / 100, // Round to 2 decimal places
    rating: metric.rating,
    delta: Math.round(metric.delta * 100) / 100,
    id: metric.id,
    navigationType: metric.navigationType,
    page: window.location.pathname,
    timestamp: Date.now(),
    userAgent: navigator.userAgent,
  };
}

/**
 * Reports a single metric to the telemetry endpoint.
 * Uses sendBeacon for reliable delivery even on page unload.
 */
function reportMetric(metric: Metric): void {
  const payload = formatMetric(metric);

  // Debug logging in development
  if (DEBUG_VITALS) {
    const color = metric.rating === 'good' ? 'green' : metric.rating === 'needs-improvement' ? 'orange' : 'red';
    console.log(
      `%c[WebVitals] ${metric.name}: ${payload.value} (${metric.rating})`,
      `color: ${color}; font-weight: bold;`
    );
  }

  // Send to API using sendBeacon for reliability
  // Falls back to fetch if sendBeacon unavailable
  const data = JSON.stringify(payload);

  if (navigator.sendBeacon) {
    // sendBeacon is more reliable for metrics that fire on page unload
    const blob = new Blob([data], { type: 'application/json' });
    navigator.sendBeacon(VITALS_ENDPOINT, blob);
  } else {
    // Fallback to fetch with keepalive
    fetch(VITALS_ENDPOINT, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: data,
      keepalive: true,
    }).catch(() => {
      // Silently fail - telemetry should not affect user experience
    });
  }
}

/**
 * Aggregates metrics in memory for batch reporting.
 * Used to reduce network requests while still capturing all metrics.
 */
const metricsBuffer: VitalsPayload[] = [];
let flushTimer: ReturnType<typeof setTimeout> | null = null;

/**
 * Buffers a metric and schedules a batch flush.
 */
function bufferMetric(metric: Metric): void {
  const payload = formatMetric(metric);
  metricsBuffer.push(payload);

  // Debug logging in development
  if (DEBUG_VITALS) {
    const color = metric.rating === 'good' ? 'green' : metric.rating === 'needs-improvement' ? 'orange' : 'red';
    console.log(
      `%c[WebVitals] ${metric.name}: ${payload.value} (${metric.rating})`,
      `color: ${color}; font-weight: bold;`
    );
  }

  // Schedule batch flush after 5 seconds of no new metrics
  if (flushTimer) clearTimeout(flushTimer);
  flushTimer = setTimeout(flushMetrics, 5000);
}

/**
 * Flushes buffered metrics to the API.
 */
function flushMetrics(): void {
  if (metricsBuffer.length === 0) return;

  const batch = [...metricsBuffer];
  metricsBuffer.length = 0;

  const data = JSON.stringify({ metrics: batch });

  if (navigator.sendBeacon) {
    const blob = new Blob([data], { type: 'application/json' });
    navigator.sendBeacon(VITALS_ENDPOINT, blob);
  } else {
    fetch(VITALS_ENDPOINT, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: data,
      keepalive: true,
    }).catch(() => {});
  }
}

/**
 * Initializes Web Vitals collection.
 * Call this once in the application entry point (main.tsx).
 * 
 * @param options.immediate - Report metrics immediately (default: false, batched)
 */
export function initWebVitals(options?: { immediate?: boolean }): void {
  const handler = options?.immediate ? reportMetric : bufferMetric;

  // Core Web Vitals (Google's key metrics)
  onLCP(handler);  // Largest Contentful Paint - loading performance
  onINP(handler);  // Interaction to Next Paint - interactivity (replaced FID)
  onCLS(handler);  // Cumulative Layout Shift - visual stability

  // Additional useful metrics
  onFCP(handler);  // First Contentful Paint - perceived loading
  onTTFB(handler); // Time to First Byte - server responsiveness

  // Ensure metrics are flushed on page unload
  window.addEventListener('visibilitychange', () => {
    if (document.visibilityState === 'hidden') {
      flushMetrics();
    }
  });

  // Also flush on beforeunload for older browsers
  window.addEventListener('beforeunload', flushMetrics);

  if (DEBUG_VITALS) {
    console.log('[WebVitals] Initialized performance monitoring');
  }
}

/**
 * Manual metric reporting for custom performance measurements.
 * Use this to track SPA route transitions, API call latencies, etc.
 * 
 * @param name - Metric name (e.g., 'route-change', 'api-latency')
 * @param value - Metric value in milliseconds
 * @param rating - Performance rating
 */
export function reportCustomMetric(
  name: string,
  value: number,
  rating: 'good' | 'needs-improvement' | 'poor' = 'good'
): void {
  const payload: VitalsPayload = {
    name: `custom.${name}`,
    value: Math.round(value * 100) / 100,
    rating,
    delta: value,
    id: `custom-${Date.now()}-${Math.random().toString(36).slice(2, 9)}`,
    navigationType: 'custom',
    page: window.location.pathname,
    timestamp: Date.now(),
    userAgent: navigator.userAgent,
  };

  metricsBuffer.push(payload);

  if (DEBUG_VITALS) {
    console.log(`%c[WebVitals] custom.${name}: ${value}`, 'color: blue;');
  }
}
